import {
  Conversation,
  Request,
  ResponseViewModel,
  ConversationResponse,
} from "~/pages/copilotMessage/types/copilot";
import {
  getPreviousRequests,
  useGetRequests,
} from "~/pages/copilotMessage/hooks/useGetRequests";
import { useGetConversations } from "~/pages/copilotMessage/hooks/useGetConversations";
import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import moment from "moment";
import { useUserLogContext } from "~/providers/UserLogProvider";
import { CopilotLogsType } from "~/pages/copilotMessage/types/copilotLogs";
import { setScrollByMikeId } from "~/pages/copilotMessage/helpers/copilotMessageElement";

import { COPILOT_EVENTS } from "~/constants/events";
import { URL_PARAM_RESPONSE_ID } from "~/constants/urlParams";
import { URL_PARAM_CONVERSATION_ID } from "~/constants/urlParams";

interface CopilotMessageContextProps {
  isConversationListExpanded: boolean;
  setIsConversationListExpanded: React.Dispatch<React.SetStateAction<boolean>>;

  conversationList: Conversation[] | undefined;
  conversationListLoading: boolean;

  selectedConversation: Conversation | undefined;
  setSelectedConversation: React.Dispatch<
    React.SetStateAction<Conversation | undefined>
  >;
  selectedConversationRef: React.MutableRefObject<Conversation | undefined>;

  conversationResponse: ConversationResponse | undefined;
  setConversationResponse: (response: ConversationResponse | undefined) => void;

  filteredLadleIdList: string[] | undefined;
  setFilteredLadleIdList: React.Dispatch<
    React.SetStateAction<string[] | undefined>
  >;
  ladleFilterOptionList: { label: string; value: string }[] | undefined;

  requests: Request[];
  responses: ResponseViewModel[];
  requestLoading: boolean;

  historyLoading: boolean;
  canDisplayPreviosMessageButton: boolean;
  getMessageHistory: () => Promise<void>;
}

// Default context values
const defaultContextValues: CopilotMessageContextProps = {
  isConversationListExpanded: true,
  setIsConversationListExpanded: () => {},
  selectedConversation: undefined,
  setSelectedConversation: () => {},
  conversationResponse: undefined,
  setConversationResponse: () => {},
  conversationList: undefined,
  conversationListLoading: false,
  filteredLadleIdList: undefined,
  setFilteredLadleIdList: () => {},
  ladleFilterOptionList: undefined,
  requests: [],
  responses: [],
  requestLoading: false,
  historyLoading: false,
  canDisplayPreviosMessageButton: true,
  selectedConversationRef: { current: undefined },
  getMessageHistory: async () => {},
};

const CopilotMessageContext =
  createContext<CopilotMessageContextProps>(defaultContextValues);

interface CopilotMessageProviderProps {
  children: ReactNode;
}

const CopilotMessageProvider: FC<CopilotMessageProviderProps> = ({
  children,
}) => {
  const { isProgramaticScrolling, insertUserLog } = useUserLogContext();

  // ==================== STATES ====================
  const [canDisplayPreviosMessageButton, setCanDisplayPreviosMessageButton] =
    useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [isConversationListExpanded, setIsConversationListExpanded] =
    useState(true);
  const [selectedConversation, setSelectedConversationState] = useState<
    Conversation | undefined
  >(undefined);
  const [filteredLadleIdList, setFilteredLadleIdList] = useState<
    string[] | undefined
  >();

  // ==================== URL MANAGEMENT ====================
  const urlManager = useMemo(
    () => ({
      // Read URL params
      read: () => {
        const searchParams = new URLSearchParams(window.location.search);
        return {
          conversationId: searchParams.get(URL_PARAM_CONVERSATION_ID),
          responseId: searchParams.get(URL_PARAM_RESPONSE_ID),
        };
      },

      // Update URL and local state
      update: (params: Record<string, string | null | undefined>) => {
        const url = new URL(window.location.href);
        let hasChanges = false;

        Object.entries(params).forEach(([key, value]) => {
          const currentValue = url.searchParams.get(key);
          if (
            (currentValue && !value) ||
            (!currentValue && value) ||
            currentValue !== value
          ) {
            hasChanges = true;
            url.searchParams.delete(key);
            if (value) url.searchParams.set(key, value);
          }
        });

        if (hasChanges) {
          window.history.pushState({}, "", url.toString());
          // Update local state
          const newParams = urlManager.read();
          setUrlParams((prev) => {
            if (
              prev.conversationId !== newParams.conversationId ||
              prev.responseId !== newParams.responseId
            ) {
              return newParams;
            }
            return prev;
          });
        }
      },

      // Sync with current URL
      sync: () => {
        const newParams = urlManager.read();
        setUrlParams((prev) => {
          if (
            prev.conversationId !== newParams.conversationId ||
            prev.responseId !== newParams.responseId
          ) {
            return newParams;
          }
          return prev;
        });
      },
    }),
    []
  );

  // URL parameters state
  const [urlParams, setUrlParams] = useState<{
    conversationId: string | null;
    responseId: string | null;
  }>(urlManager.read());

  const [relatedResponses, setRelatedResponses] = useState<
    Record<string, ResponseViewModel[]>
  >({});

  // ==================== REFS ====================
  const selectedConversationRef = useRef(selectedConversation);
  selectedConversationRef.current = selectedConversation;
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();

  // Derived searchedHeat from URL params
  const conversationResponse = useMemo((): ConversationResponse | undefined => {
    if (urlParams.conversationId) {
      return {
        conversationId: urlParams.conversationId,
        responseId: urlParams.responseId || undefined,
      } as ConversationResponse;
    }
    return undefined;
  }, [urlParams.conversationId, urlParams.responseId]);

  // ==================== DERIVED STATES ====================
  const { conversationList, conversationListLoading } = useGetConversations();
  const { requests, responses, requestLoading } = useGetRequests(
    selectedConversation?.id || "",
    conversationResponse?.responseId,
    relatedResponses[conversationResponse?.responseId ?? ""]
  );

  // Filter conversation list
  const conversationListForRender = useMemo(() => {
    if (!filteredLadleIdList || filteredLadleIdList.length === 0)
      return conversationList;
    return conversationList?.filter((conversation) =>
      filteredLadleIdList.includes(conversation.assetId)
    );
  }, [filteredLadleIdList, conversationList]);

  // Create options list for ladle filter
  const ladleFilterOptionList = useMemo(
    () =>
      conversationList
        ?.map((conversation) => ({
          label: conversation.assetName,
          value: conversation.assetId,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [conversationList]
  );

  // ==================== SCROLL MANAGEMENT ====================
  const scrollToResponse = useCallback(
    (responseId: string | undefined, delay = 2000) => {
      if (!responseId) return;

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      setScrollByMikeId(responseId, isProgramaticScrolling);
      scrollTimeoutRef.current = setTimeout(() => {
        setScrollByMikeId(responseId, isProgramaticScrolling);
      }, delay);
    },
    [isProgramaticScrolling]
  );

  // ==================== CONVERSATION MANAGEMENT ====================
  const setSelectedConversation = useCallback(
    (newConversation: React.SetStateAction<Conversation | undefined>) => {
      const conversation =
        typeof newConversation === "function"
          ? newConversation(selectedConversation)
          : newConversation;

      if (conversation?.id !== selectedConversation?.id) {
        setSelectedConversationState(conversation);

        if (conversation?.id) {
          urlManager.update({
            conversationId: conversation.id,
            responseId: null,
          });
        }
      }
    },
    [selectedConversation, urlManager]
  );

  const setConversationResponse = useCallback(
    (response: ConversationResponse | undefined) => {
      if (response) {
        urlManager.update({
          conversationId: response.conversationId,
          responseId: response.responseId || undefined,
        });
        setRelatedResponses(response.responsesMap || {});
      } else {
        urlManager.update({ responseId: null });
      }
    },
    [urlManager]
  );

  // ==================== MESSAGE HISTORY ====================
  const getMessageHistory = useCallback(async () => {
    insertUserLog(CopilotLogsType.PreviousMessagesClicked);
    if (!requests?.length) return;

    const sortedRequests = [...requests].sort((a, b) =>
      moment(a.operationDate).diff(moment(b.operationDate))
    );

    setHistoryLoading(true);

    try {
      const availablePrevious = await getPreviousRequests(
        selectedConversation?.id || "",
        moment(sortedRequests[0].operationDate).toISOString()
      );
      setCanDisplayPreviosMessageButton(availablePrevious);
    } catch (error) {
      setCanDisplayPreviosMessageButton(false);
    } finally {
      setHistoryLoading(false);
    }
  }, [selectedConversation?.id, requests, insertUserLog]);

  // ==================== EFFECTS ====================

  // Reset previous message button when conversation changes
  useEffect(() => {
    setCanDisplayPreviosMessageButton(true);
  }, [selectedConversation?.id]);

  // Initialize URL params and listen for browser navigation
  useEffect(() => {
    urlManager.sync();

    const handlePopState = () => urlManager.sync();
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [urlManager]);

  // Handle URL changes and conversation/scroll logic
  useEffect(() => {
    if (!conversationList?.length || conversationListLoading) return;

    const { conversationId, responseId } = urlParams;

    if (conversationId) {
      const conversation = conversationList.find(
        (c) => c.id === conversationId
      );

      if (conversation && selectedConversation?.id !== conversation.id) {
        setSelectedConversationState(conversation);
      }

      // Handle scroll for any responseId
      if (responseId) {
        scrollToResponse(responseId);
      }
    } else if (!selectedConversation && conversationList.length > 0) {
      // Select first conversation if none selected
      setSelectedConversationState(conversationList[0]);
      urlManager.update({ conversationId: conversationList[0].id });
    }
  }, [
    conversationList,
    conversationListLoading,
    urlParams,
    selectedConversation,
    scrollToResponse,
    urlManager,
  ]);

  // Handle notification clicks
  useEffect(() => {
    const handleNotificationClick = (e: CustomEvent) => {
      const { conversationId, responseId } = e.detail;
      if (!conversationId || !conversationList?.length) return;

      const conversation = conversationList.find(
        (c) => c.id === conversationId
      );
      if (!conversation) return;

      if (conversation.id !== selectedConversationRef.current?.id) {
        setSelectedConversationState(conversation);
      }

      if (responseId) {
        urlManager.update({ conversationId: conversation.id, responseId });
      }
    };

    window.addEventListener(
      COPILOT_EVENTS.NOTIFICATION_CLICKED,
      handleNotificationClick as EventListener
    );
    return () =>
      window.removeEventListener(
        COPILOT_EVENTS.NOTIFICATION_CLICKED,
        handleNotificationClick as EventListener
      );
  }, [conversationList, urlManager]);

  // Cleanup scroll timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // ==================== CONTEXT VALUE ====================
  const contextValue = useMemo(
    () => ({
      isConversationListExpanded,
      setIsConversationListExpanded,
      conversationList: conversationListForRender,
      conversationListLoading,
      selectedConversation,
      selectedConversationRef,
      setSelectedConversation,
      conversationResponse,
      setConversationResponse,
      filteredLadleIdList,
      setFilteredLadleIdList,
      ladleFilterOptionList,
      requests: requests ?? [],
      responses: responses ?? [],
      requestLoading,
      historyLoading,
      canDisplayPreviosMessageButton,
      getMessageHistory,
    }),
    [
      isConversationListExpanded,
      conversationListForRender,
      conversationListLoading,
      selectedConversation,
      selectedConversationRef,
      setSelectedConversation,
      conversationResponse,
      setConversationResponse,
      filteredLadleIdList,
      ladleFilterOptionList,
      requests,
      responses,
      requestLoading,
      historyLoading,
      canDisplayPreviosMessageButton,
      getMessageHistory,
    ]
  );

  return (
    <CopilotMessageContext.Provider value={contextValue}>
      {children}
    </CopilotMessageContext.Provider>
  );
};

export { CopilotMessageContext, CopilotMessageProvider };
